import React, { Component, Fragment, useEffect, useRef, useState } from 'react';
import { withRouter } from 'react-router';
import { Redirect, Route, Switch } from 'react-router-dom';
import PropTypes from 'prop-types';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TableBody, TableRow } from '@mui/material';
import axios from 'axios';
import { Formik } from 'formik';
import { merge } from 'lodash';
import { AccountLockOutline } from 'mdi-material-ui';
import queryString from 'query-string';
import urljoin from 'url-join';
import { v4 as uuidv4 } from 'uuid';
import * as Yup from 'yup';

import colors from '~/assets/colors.module.scss';
import styles, { useStyles } from '~/assets/styles';
import { withStyles } from '~/assets/withStyles';
import { AiChat } from '~/components/AiChat/containers/AiChat';
import { ChatProvider } from '~/components/AiChat/hooks/useChat';
import { claimPageBarClass } from '~/components/ClaimPage/ClaimPageBar/ClaimPageBarUtils';
import { isConfigurableClaimBarEnabled } from '~/components/ClaimPage/claimPageVariant';
import { ClaimInsightsCountContextProvider } from '~/components/ClaimPage/CliveApp/CliveInsights/hooks/useClaimInsightsCount';
import { ConductorStatusProvider } from '~/components/ClaimPage/CliveApp/hooks/useConductorStatusObservation';
import CreateLikeClaimDialog from '~/components/ClaimPage/CreateLikeClaim/CreateLikeClaimDialog';
import ExternalClaimNumber from '~/components/ClaimPage/ExternalClaimNumber';
import SendEmailCommunicationCardContainer from '~/components/communications/EmailCommunicationCard/SendEmailCommunicationCardContainer';
import DocumentPhoneCallCommunicationCardContainer, {
  NewPhoneCallCommunicationCard,
} from '~/components/communications/PhoneCall/components/PhoneCallCommunicationCard/PhoneCallCommunicationCard';
import { PERMISSION_ACTIONS, RestrictedPermissions } from '~/components/core';
import ActionMenuButton from '~/components/core/ActionMenuButton/ActionMenuButton';
import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Table from '~/components/core/Atomic/Table';
import TableCell from '~/components/core/Atomic/TableCell';
import Typography from '~/components/core/Atomic/Typography';
import CancelButton from '~/components/core/Buttons/CancelButton';
import { useMinimizedDialogs } from '~/components/core/MinimizedBar/Context';
import PermissionsButtonWrapper from '~/components/core/Permissions/PermissionsButtonWrapper';
import { isPermissionsEnabled, PERMISSION_VERBS } from '~/components/core/Permissions/PermissionUtils';
import SafeComponent, { CriticalityLevel, withSafeComponentPage } from '~/components/core/SafeComponent';
import { FallbackErrorPage } from '~/components/core/SafeComponent/FallbackErrorPage';
import SuccessMessageDialog from '~/components/core/SuccessMessageDialog';
import Text from '~/components/core/TextComponents/Text';
import RequestDocumentsLinkDialog from '~/components/DocumentRequests/RequestDocumentsLinkDialog';
import { TEXTS as DOCUMENT_REQUESTS_TEXTS } from '~/components/DocumentRequests/texts';
import {
  useHasMultiplePermissions,
  useHasPermission,
  useSanitizeByPermissions,
} from '~/components/hooks/useHasPermission';
import { useLobConfiguration } from '~/components/hooks/useLobConfiguration';
import {
  CommunicationIcon,
  DocumentPhoneIcon,
  FileArrowUpIcon,
  HashIcon,
  NoteIcon,
  UserLockIcon,
} from '~/components/icons';
import CopyFileIcon from '~/components/icons/CopyFileIcon';
import HistoryIcon from '~/components/icons/HistoryIcon';
import { isFutureDateOfLossAllowed } from '~/components/IncidentConfiguration/utils';
import { LegalActionsContextProvider, useLegalActions } from '~/components/LegalActions/hooks/useLegalActions';
import CreateEditNoteDialog from '~/components/Notes/CreateEditNoteDialog';
import { RelatedClaimsCardDialog } from '~/components/RelatedClaimsContainer';
import {
  CliveClaimContextProvider,
  useCliveClaimContext,
} from '~/components/SystemConfiguration/CliveConfiguration/CliveClaimContext';
import { SorClaimLockBanner } from '~/components/SystemOfRecord/SorClaimLockBanner';
import { DatePickerTextFieldFormik, TimePickerTextFieldFormik } from '~/components/TextFieldFormik';
import Popover from '~/components/ui/atoms/Popover';
import usePopoverState from '~/components/ui/atoms/Popover/usePopoverState';
import { isoDateToUs, timeToLocalTime } from '~/DateTimeUtils';
import { MIXPANEL_EVENT_SOURCES, MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import { isUserAdjuster, isUserReadOnly } from '~/UserUtils';
import {
  compose,
  isFeatureEnabled,
  isMarshmallowPolicy,
  isPolicyNotFound,
  isQoverClaim,
  isTscClaim,
  reportAxiosError,
  reportErrorInProductionOrThrow,
} from '~/Utils';
import { isClaimWriteDisabled } from '~/Utils/ClaimUtils';
import cn from '~/Utils/cn';
import { getLobIcon } from '~/Utils/lobUtils';

import CardDialog from '../CardDialog';
import { useClaim, withClaim } from '../ClaimContainer';
import ClaimOwnerWithEdit from '../ClaimOwnerWithEdit';
import { ClaimQaCard, ClaimQAContainer } from '../ClaimQA';
import ClaimSummaryPhoneRep from '../ClaimSummaryPhoneRep';
import CloseClaimContainer from '../CloseClaimContainer';
import mixpanel from '../CmsMain/mixpanel';
import { getAllSearchableContactRoles } from '../communications/ContactUtils';
import DocumentPhysicalMailCommunicationCardContainer from '../communications/PhysicalMailCommunicationCard';
import SendSmsCommunicationCardContainer from '../communications/SmsCommunicationCard/SmsCommunicationCard';
import NewVideoCommunicationCard from '../communications/VideoCommunication/NewVideoCommunicationCard';
import { ContactEntity } from '../Contact';
import DocumentIncomingPhoneCallContainer from '../DocumentIncomingPhoneCallContainer';
import { ExportClaimDialog } from '../ExportClaimContainer';
import { useCms, withCmsContext } from '../hooks/useCms';
import HoverActionField from '../HoverActionField';
import HoverChangeField from '../HoverChangeField';
import {
  EmailIcon,
  FaxIcon,
  InternalCommunicationIcon,
  MailBoxIcon,
  PhoneIcon,
  ReminderIcon,
  SmsIcon,
  VideoIcon,
} from '../icons/notifications';
import { AddInternalCommunicationDialog } from '../InternalCommunication';
import { LimitAccessDialog } from '../LimitClaimAccessContainer';
import useOrganization, { withOrganization } from '../OrganizationContext';
import { AddReminderDialog } from '../ReminderNotificationContainer';
import { useClaimSearch } from '../SystemConfiguration/Integrations/ClaimSearch/hooks/useClaimSearch';

import useClaimActiveTab from './hooks/useClaimActiveTab';
import useClaimSidebarTabs from './hooks/useClaimSideBarTabs';
import NewUIClaimPageBar from './ClaimPageBar';
import { ClaimSidebar } from './ClaimSidebar';

// TODO - Handle phone rep/read only

class ClaimPage extends Component {
  state = {
    tabValue: false,
  };

  updatePageTitle = () => {
    const { claim, setPageTitle, classes, isLoading } = this.props;
    setPageTitle(
      <ClaimTitle claim={claim} classes={classes} isLoading={isLoading} />,
      `Claims / ${claim.claim_id_display} / — Five Sigma CMS`
    );
  };

  componentDidUpdate(prevProps) {
    const { claim } = this.props;

    if (
      prevProps.claim.is_closed !== claim.is_closed ||
      prevProps.claim.close_note !== claim.close_note ||
      prevProps.claim.close_reason_display_name !== claim.close_reason_display_name ||
      prevProps.claim.subtype !== claim.subtype ||
      getMedicareEligibleStatus(prevProps.claim) !== getMedicareEligibleStatus(claim)
    ) {
      this.updatePageTitle();
    }
  }

  render() {
    return (
      <CliveClaimContextProvider>
        <ChatProvider>
          <LegalActionsContextProvider>
            <ClaimInsightsCountContextProvider>
              <ConductorStatusProvider>
                <ClaimPageFunctionalInner {...this.props} updatePageTitle={this.updatePageTitle} />
              </ConductorStatusProvider>
            </ClaimInsightsCountContextProvider>
          </LegalActionsContextProvider>
        </ChatProvider>
      </CliveClaimContextProvider>
    );
  }
}

ClaimPage.propTypes = {
  classes: PropTypes.object.isRequired, // from styles context
  claim: PropTypes.object.isRequired, // from claim context
  onClaimUpdate: PropTypes.func.isRequired, // from claim context
  onAsyncClaimUpdate: PropTypes.func.isRequired, // from claim context
  user: PropTypes.object.isRequired, // from cms context
  setPageTitle: PropTypes.func.isRequired, // from cms context
  match: PropTypes.object.isRequired, // from routes context
  location: PropTypes.object.isRequired, // from routes context
  userOrganization: PropTypes.object.isRequired, // from routes context
  isLoading: PropTypes.bool.isRequired, // from organization context
};

const ClaimPageFunctionalInner = ({ claim, match, location, user, userOrganization, updatePageTitle }) => {
  const { classes } = useStyles();
  const newClaimPageBarEnabled = isConfigurableClaimBarEnabled(userOrganization);
  const [qaInProgress, setQaInProgress] = useState(null);

  React.useEffect(() => {
    const searchParams = queryString.parse(location.search);
    if (searchParams.qa) {
      setQaInProgress({ exposureId: parseInt(searchParams.qa), shouldCloseWindow: true });
    }

    updatePageTitle();
  }, [location, updatePageTitle]);

  const { reloadLegalActions } = useLegalActions();
  const tabs = useClaimSidebarTabs();
  const { activeTab, shouldDisplayEmbeddedChat } = useClaimActiveTab();
  const { isLoading: isCliveLoading } = useCliveClaimContext();

  const shouldPersistClaimTabs = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.PERFORMANCE_PERSIST_CLAIM_TABS
  );

  if (isPermissionsEnabled(userOrganization) || isUserAdjuster(user) || isUserReadOnly(user)) {
    const claimInternalPath = tabs.map((tab) => urljoin(match.path, tab.url));
    return (
      <RestrictedPermissions>
        {newClaimPageBarEnabled ? (
          <NewUIClaimPageBar />
        ) : (
          <ClaimPageBar onRequestExposureQa={(exposureId) => setQaInProgress({ qaInProgress: { exposureId } })} />
        )}
        <SorClaimLockBanner className="mx-16 mt-8" />
        <div style={{ flexGrow: 1 }}>
          <Grid container wrap="nowrap" style={{ height: '100%' }}>
            <Grid item>
              <ClaimSidebar
                tabs={tabs}
                location={location}
                match={match}
                claim={claim}
                reloadLegalActions={reloadLegalActions}
              />
            </Grid>

            <Grid item container direction="column" style={{ height: '100%', position: 'relative' }}>
              <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, overflow: 'auto' }}>
                <Switch>
                  <Redirect exact from={`${match.path}/`} to={urljoin(match.path, tabs[0].url)} />
                  {shouldPersistClaimTabs ? (
                    <Route
                      key="main"
                      path={claimInternalPath}
                      render={(routerProps) => {
                        if (activeTab) {
                          // We cant call setPageTitleHere - and we don't have to
                          document.title = `Claims / ${claim.claim_id_display} / ${activeTab.label} — Five Sigma CMS`;
                        }
                        return (
                          <TabsWrapper
                            routerProps={routerProps}
                            claim={claim}
                            tabs={tabs}
                            activeTab={activeTab}
                            shouldDisplayEmbeddedChat={shouldDisplayEmbeddedChat}
                          />
                        );
                      }}
                    />
                  ) : (
                    tabs.map((tab) => (
                      <Route
                        key={tab.url}
                        path={urljoin(match.path, tab.url)}
                        render={(routerProps) => {
                          // We cant call setPageTitleHere - and we don't have to
                          document.title = `Claims / ${claim.claim_id_display} / ${tab.label} — Five Sigma CMS`;

                          return (
                            <div
                              className={`${shouldDisplayEmbeddedChat ? (activeTab?.url === 'osha' ? 'pb-[100px]' : 'pb-[75px]') : ''}`}
                            >
                              <tab.component classes={classes} {...routerProps} {...(tab.componentProps ?? {})} />
                            </div>
                          );
                        }}
                      />
                    ))
                  )}

                  {/* Clive-gated tabs join `tabs` only once Clive loads; redirecting before would bounce them to the first tab */}
                  {!isCliveLoading ? <Redirect to={match.path} /> : null}
                </Switch>
              </div>
            </Grid>
          </Grid>
        </div>
        {qaInProgress &&
        qaInProgress.exposureId &&
        claim?.exposures?.find((exposure) => exposure.id === qaInProgress.exposureId) ? (
          <ClaimQaCard
            claim={claim}
            exposure={claim.exposures.find((exposure) => exposure.id === qaInProgress.exposureId)}
            onSubmit={() => {
              setQaInProgress({ qaInProgress: undefined });
              if (qaInProgress.shouldCloseWindow) {
                window.close();
              }
            }}
            onClose={() => setQaInProgress({ qaInProgress: undefined })}
          />
        ) : null}
      </RestrictedPermissions>
    );
  } else {
    return <PhoneRepClaimPage claim={claim} classes={classes} />;
  }
};

ClaimPageFunctionalInner.propTypes = {
  ...ClaimPage.propTypes,
  updatePageTitle: PropTypes.func.isRequired,
};

function ClaimPageBar(props) {
  const { onRequestExposureQa } = props;
  const { classes } = useStyles();
  const { claim, onClaimUpdate } = useClaim();
  const { user, userOrganization } = useCms();
  const { shouldDisplayEmbeddedChat } = useClaimActiveTab();

  return (
    <div className={claimPageBarClass}>
      <Grid container direction="row" justify="space-between" alignItems="center" wrap="nowrap">
        <Grid item>
          <Grid container direction="row" justify="flex-start" alignItems="center" wrap="nowrap" spacing={2}>
            {claim.policy.insured_contact_id && ( // For self-insured organizations there is noe insured contact
              <Grid item>
                <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>{isQoverClaim(claim) ? 'Policy Holder' : 'Named Insured:'}</strong>
                  <ContactEntity
                    classes={classes}
                    inline
                    contactId={claim.policy.insured_contact_id}
                    contactDisplayName={claim.policy.insured_contact_full_name}
                  />
                </div>
              </Grid>
            )}
            <Grid item>
              <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
                <strong>Preferred Contact: </strong>
                <PreferredContactContainer claim={claim} onClaimUpdate={onClaimUpdate} />
              </div>
            </Grid>

            <Grid item>
              <div style={{ padding: 4, display: 'inline-flex', alignItems: 'center' }}>
                <strong>
                  {(['wc_claim', 'gl_claim', 'pet_claim'].includes(claim.type) && 'DOI:') ||
                    (isQoverClaim(claim) && 'DOL:') ||
                    'TOL'}
                </strong>
                &nbsp;
                <span>
                  <TimeOfLossContainer claim={claim} onClaimUpdate={onClaimUpdate} />
                </span>
              </div>
            </Grid>

            {!isPolicyNotFound(claim) && (
              <Grid item>
                <span>
                  <strong>Policy #: </strong>
                  <span style={{ whiteSpace: 'nowrap' }}>{claim.policy.policy_number}</span>
                  {claim.policy.is_manually_filled ? ' (Manually filled)' : ''}
                </span>
              </Grid>
            )}
            {!isPolicyNotFound(claim) && isMarshmallowPolicy(claim.policy) && (
              <Grid item>
                <span>
                  <strong>Package: </strong>
                  <span style={{ whiteSpace: 'nowrap' }}>{claim.policy.policy_type}</span>
                </span>
              </Grid>
            )}

            {user.is_org_level_supervisor &&
              isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.QA_DASHBOARD) && (
                <Grid item>
                  <ClaimQAContainer onRequestExposureQa={onRequestExposureQa} />
                </Grid>
              )}

            {isQoverClaim(claim) && (
              <>
                <Grid item>
                  <span>
                    <strong>Partner: </strong>
                    <span style={{ whiteSpace: 'nowrap' }}>{claim.policy.partner}</span>
                  </span>
                </Grid>

                <Grid item>
                  <span>
                    <strong>Product: </strong>
                    <span style={{ whiteSpace: 'nowrap' }}>{claim.policy.product}</span>
                  </span>
                </Grid>
              </>
            )}

            {!isTscClaim(claim) && (
              <Grid item>
                <div style={{ display: 'inline-flex', alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong>File Owner:</strong> &nbsp;
                  <span>
                    <ClaimOwnerWithEdit classes={classes} claim={claim} />
                  </span>
                </div>
              </Grid>
            )}
            {userOrganization.sub_organizations_enabled && claim.policy.sub_organization && (
              <Grid item>
                <span>
                  <strong>Sub Org:</strong> &nbsp;
                  <span>{claim.policy.sub_organization.name}</span>
                </span>
              </Grid>
            )}
          </Grid>
        </Grid>

        <Grid
          item
          container
          direction="row"
          justify="flex-end"
          alignItems="center"
          wrap="nowrap"
          spacing={2}
          style={{ flexBasis: 0 }}
        >
          <Grid item>
            <ClaimActionButtonContainer />
          </Grid>
          <Grid item>
            <CloseClaimContainer />
          </Grid>
        </Grid>
      </Grid>
      {shouldDisplayEmbeddedChat ? (
        <SafeComponent>
          <AiChat />
        </SafeComponent>
      ) : null}
    </div>
  );
}

ClaimPageBar.propTypes = {
  onRequestExposureQa: PropTypes.func.isRequired,
  shouldDisplayEmbeddedChat: PropTypes.bool,
};

const TabsWrapper = ({ routerProps, tabs, activeTab, shouldDisplayEmbeddedChat }) => {
  const { claim } = useClaim(null);
  const { classes } = useStyles();
  const [displayedUrls, setDisplayedUrls] = useState([]);

  useEffect(() => {
    setDisplayedUrls([]);
  }, [claim]);

  tabs.forEach((tab) => {
    const isActive = tab.url === activeTab?.url;
    const shouldLoadTab = isActive || tab.preLoad;
    if (shouldLoadTab && !displayedUrls.includes(tab.url) && tab.persist) {
      setDisplayedUrls((prevState) => [...prevState, tab.url]);
    }
  });

  return (
    <>
      {tabs.map((tab) => {
        const isActive = tab.url === activeTab.url;
        const showPadding = isActive && shouldDisplayEmbeddedChat;

        if (isActive || displayedUrls.includes(tab.url)) {
          return (
            <span
              key={tab.url}
              className={cn(
                {
                  block: isActive,
                  hidden: !isActive,
                },
                showPadding ? (activeTab?.url === 'osha' ? 'pb-[100px]' : 'h-full pb-[75px]') : 'h-full'
              )}
            >
              <tab.component
                classes={classes}
                {...routerProps}
                {...(tab.componentProps ?? {})}
                isTabActive={isActive}
              />
            </span>
          );
        }
        return null;
      })}
    </>
  );
};

const TabType = PropTypes.shape({
  url: PropTypes.string,
  componentProps: PropTypes.object,
  component: PropTypes.elementType,
});

TabsWrapper.propTypes = {
  routerProps: PropTypes.object,
  activeTab: TabType,
  tabs: PropTypes.arrayOf(TabType),
  shouldDisplayEmbeddedChat: PropTypes.bool,
};

function ClaimActionButtonContainer() {
  const { classes } = useStyles();
  const { triggerProps, popoverProps, isOpen, close } = usePopoverState();
  const { claim, onAsyncClaimUpdate } = useClaim(null);
  const { user, userOrganization } = useCms();
  const { outgoingPhysicalMailMethod, isClaimSearchEnabled, isDyteVideoEnabled } = useOrganization();
  const [actionDialogDetails, setActionDialogDetails] = useState(null);
  const dialogIdToHandleCloseRef = useRef({});
  const { validateIsReporting } = useClaimSearch();
  const { add } = useMinimizedDialogs();
  const [exportJobsData, setExportJobsData] = useState(null);
  const activeExportCount = exportJobsData?.active_export_count;
  const isDocumentRequestsEnabled = isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.DOCUMENT_REQUESTS);
  // Same gate the Export menu item uses, because the endpoint carries the same EXPORT_CLAIM/WRITE
  // permission: without it a user who cannot export would get a permission-denied dialog (via
  // reportAxiosError's 403 handling) just for opening the actions menu.
  const canExportClaim = useHasPermission({
    action: PERMISSION_ACTIONS.EXPORT_CLAIM,
    verb: PERMISSION_VERBS.WRITE,
  });
  const canCreateLikeClaim = useHasPermission({
    action: PERMISSION_ACTIONS.CREATE_LIKE_CLAIM,
    verb: PERMISSION_VERBS.WRITE,
  });

  // A large claim export runs in the background. When the actions menu opens, refresh how many
  // exports are already in progress so we can surface a non-blocking counter next to the Export
  // action.
  useEffect(() => {
    if (!isOpen || !canExportClaim) {
      return undefined;
    }

    let active = true;

    const fetchExportJobs = async () => {
      try {
        const { data } = await axios.get(`/api/v1/claims/${claim.id}/export-jobs`);
        if (active) {
          setExportJobsData(data);
        }
      } catch (error) {
        if (active) {
          await reportAxiosError(error);
        }
      }
    };

    fetchExportJobs();

    // Ignore an in-flight response that resolves after the menu reopens or the component unmounts,
    // so a stale fetch cannot overwrite fresher export-job state.
    return () => {
      active = false;
    };
  }, [isOpen, claim.id, canExportClaim]);

  // TODO should be integrated better with the components as their dialog should no be in the popup

  const updateClaimAndCloseActionDialog = async ({ minimizedDialogId } = {}) => {
    await onAsyncClaimUpdate();
    if (minimizedDialogId) {
      await handleMinimizedDialogClose(minimizedDialogId);
    } else {
      setActionDialogDetails(null);
    }
  };

  const handleMinimizedDialogClose = (id) => {
    const handleClose = dialogIdToHandleCloseRef.current[id];
    if (handleClose) {
      handleClose();
      delete dialogIdToHandleCloseRef.current[id];
    }
  };

  const actions = [
    {
      label: 'Create Note',
      shouldDisplay: true,
      IconComponent: NoteIcon,
      DialogComponent: CreateEditNoteDialog,
      dialogComponentProps: {
        claimId: claim.id,
        onClose: () => setActionDialogDetails(false),
        onSubmitNote: updateClaimAndCloseActionDialog,
      },
      permissions: { action: PERMISSION_ACTIONS.NOTE, verb: PERMISSION_VERBS.WRITE },
      extendMinimizedDialogProps: (minimizedDialogId) => ({
        barHeader: 'New Note',
        type: 'NOTE',
        dialogProps: {
          onClose: () => handleMinimizedDialogClose(minimizedDialogId),
          onSubmitNote: async () => await updateClaimAndCloseActionDialog({ minimizedDialogId }),
        },
      }),
    },
    {
      label: 'Create Notification',
      shouldDisplay: true,
      IconComponent: ReminderIcon,
      DialogComponent: AddReminderDialog,
      dialogComponentProps: {
        open: true,
        onCancel: () => setActionDialogDetails(false),
        onSubmit: updateClaimAndCloseActionDialog,
      },
      permissions: { action: PERMISSION_ACTIONS.NOTIFICATION, verb: PERMISSION_VERBS.WRITE },
      extendMinimizedDialogProps: (minimizedDialogId) => ({
        barHeader: 'Create Notification',
        type: 'REMINDER',
        dialogProps: {
          onCancel: () => handleMinimizedDialogClose(minimizedDialogId),
          onSubmit: async () => await updateClaimAndCloseActionDialog({ minimizedDialogId }),
        },
      }),
    },
    {
      label: 'Make Phone Call',
      shouldDisplay: !!user.is_call_center_allowed,
      IconComponent: PhoneIcon,
      DialogComponent: NewPhoneCallCommunicationCard,
      dialogComponentProps: {
        onCancel: () => setActionDialogDetails(false),
        onNewPhoneCallCreated: updateClaimAndCloseActionDialog,
      },
      mixpanelProps: {
        source: MIXPANEL_EVENT_SOURCES.CLAIM_ACTIONS_NEW_PHONE_CALL,
        mixpanel_event: MIXPANEL_EVENTS.NEW_PHONE_CALL_COMMUNICATION_CLICKED,
      },
    },
    {
      label: 'Make Video Call',
      shouldDisplay: isDyteVideoEnabled,
      IconComponent: VideoIcon,
      DialogComponent: NewVideoCommunicationCard,
      dialogComponentProps: {
        claim,
        onCancel: () => setActionDialogDetails(false),
        onNewVideoCallCreated: updateClaimAndCloseActionDialog,
      },
    },
    {
      label: DOCUMENT_REQUESTS_TEXTS.ACTION_LABEL,
      shouldDisplay: isDocumentRequestsEnabled && !claim.is_closed,
      IconComponent: FileArrowUpIcon,
      DialogComponent: RequestDocumentsLinkDialog,
      // The claim refresh also reloads the Documents tab's Requests list when it is open.
      dialogComponentProps: { claim, onClose: updateClaimAndCloseActionDialog },
      permissions: { action: PERMISSION_ACTIONS.CLAIM_DOCUMENT, verb: PERMISSION_VERBS.WRITE },
    },
    {
      label: 'Document Phone Call',
      shouldDisplay: true,
      IconComponent: DocumentPhoneIcon,
      DialogComponent: DocumentPhoneCallCommunicationCardContainer,
      dialogComponentProps: {
        claim,
        onCancel: () => setActionDialogDetails(false),
        onDocumentPhoneCall: updateClaimAndCloseActionDialog,
      },
      extendMinimizedDialogProps: (minimizedDialogId) => ({
        barHeader: 'Document Phone Call',
        type: 'PHONE',
        dialogProps: {
          onCancel: () => handleMinimizedDialogClose(minimizedDialogId),
          onDocumentPhoneCall: async () => await updateClaimAndCloseActionDialog({ minimizedDialogId }),
        },
      }),
      mixpanelProps: {
        source: MIXPANEL_EVENT_SOURCES.CLAIM_ACTIONS_DOCUMENT_NEW_PHONE_CALL,
        mixpanel_event: MIXPANEL_EVENTS.DOCUMENT_NEW_PHONE_CALL_COMMUNICATION_CLICKED,
      },
    },
    {
      label: 'Send Internal Communication',
      shouldDisplay: true,
      IconComponent: CommunicationIcon,
      DialogComponent: AddInternalCommunicationDialog,
      dialogComponentProps: {
        open: true,
        claim,
        icObject: { type: 'claim', claim },
        onCancel: () => setActionDialogDetails(false),
        onSubmit: updateClaimAndCloseActionDialog,
      },
      extendMinimizedDialogProps: (minimizedDialogId) => ({
        barHeader: 'Send Internal Communication',
        type: 'INTERNAL_COMMUNICATION',
        dialogProps: {
          onCancel: () => handleMinimizedDialogClose(minimizedDialogId),
          onSubmit: async () => await updateClaimAndCloseActionDialog({ minimizedDialogId }),
        },
      }),
      permissions: { action: PERMISSION_ACTIONS.NOTIFICATION, verb: PERMISSION_VERBS.WRITE },
    },
    {
      label: 'Send Text Message',
      shouldDisplay: !!user.is_sending_sms_allowed,
      IconComponent: SmsIcon,
      DialogComponent: SendSmsCommunicationCardContainer,
      dialogComponentProps: {
        onCancel: () => setActionDialogDetails(false),
        onSendSms: updateClaimAndCloseActionDialog,
      },
      mixpanelProps: {
        source: MIXPANEL_EVENT_SOURCES.CLAIM_ACTIONS_NEW_SMS,
        mixpanel_event: MIXPANEL_EVENTS.NEW_SMS_COMMUNICATION_CLICKED,
      },
      extendMinimizedDialogProps: (minimizedDialogId) => ({
        barHeader: 'New SMS',
        type: 'SMS',
        dialogProps: {
          onCancel: () => handleMinimizedDialogClose(minimizedDialogId),
          onSendSms: async () => await updateClaimAndCloseActionDialog({ minimizedDialogId }),
        },
      }),
    },
    {
      label: 'Send Email Message',
      shouldDisplay: true,
      IconComponent: EmailIcon,
      DialogComponent: SendEmailCommunicationCardContainer,
      dialogComponentProps: {
        onClose: () => setActionDialogDetails(false),
        onSendEmail: updateClaimAndCloseActionDialog,
        shouldDisplaySupervisorSenderSelection: true,
      },
      extendMinimizedDialogProps: (minimizedDialogId) => ({
        barHeader: 'New Email',
        type: 'EMAIL',
        dialogProps: {
          onClose: () => handleMinimizedDialogClose(minimizedDialogId),
          onSendEmail: async () => await updateClaimAndCloseActionDialog({ minimizedDialogId }),
        },
      }),
      mixpanelProps: {
        source: MIXPANEL_EVENT_SOURCES.CLAIM_ACTIONS_NEW_EMAIL,
        mixpanel_event: MIXPANEL_EVENTS.NEW_EMAIL_COMMUNICATION_CLICKED,
      },
    },

    {
      label: `${outgoingPhysicalMailMethod ? 'Send' : 'Document'} Physical Mail`,
      shouldDisplay: true,
      IconComponent: MailBoxIcon,
      DialogComponent: DocumentPhysicalMailCommunicationCardContainer,
      dialogComponentProps: {
        claim,
        onCancel: () => setActionDialogDetails(false),
        onDocumentPhysicalMail: updateClaimAndCloseActionDialog,
      },
      mixpanelProps: outgoingPhysicalMailMethod
        ? {
            source: MIXPANEL_EVENT_SOURCES.CLAIM_ACTIONS_NEW_PHYSICAL_MAIL,
            mixpanel_event: MIXPANEL_EVENTS.NEW_PHYSICAL_MAIL_COMMUNICATION_CLICKED,
          }
        : undefined,
    },
    {
      label: 'Export/Print Claim',
      shouldDisplay: true,
      IconComponent: FaxIcon,
      DialogComponent: ExportClaimDialog,
      dialogComponentProps: {
        onClose: () => setActionDialogDetails(false),
      },
      isAllowedForReadOnly: true,
      adornment: activeExportCount > 0 ? <Chip size="small" label={`${activeExportCount} in progress`} /> : null,
      permissions: { action: PERMISSION_ACTIONS.EXPORT_CLAIM, verb: PERMISSION_VERBS.WRITE },
    },
    {
      label: claim.is_access_limited ? 'Enable External Users' : 'Lock External Users',
      shouldDisplay: true,
      IconComponent: UserLockIcon,
      DialogComponent: LimitAccessDialog,
      dialogComponentProps: {
        claim,
        onClose: () => setActionDialogDetails(false),
        onUpdate: updateClaimAndCloseActionDialog,
      },
    },
    {
      label: 'Report to ClaimSearch',
      shouldDisplay:
        isClaimSearchEnabled && validateIsReporting(userOrganization.id, claim.policy.sub_organization?.id),
      IconComponent: InternalCommunicationIcon,
      iconColor: colors.buttonSelected,
      iconSize: 20,
      DialogComponent: ClaimSearchDialog,
      dialogComponentProps: {
        claimIdDisplay: claim.claim_id_display,
        onClose: () => setActionDialogDetails(false),
        subOrganizationId:
          userOrganization.sub_organizations_enabled && claim.policy.sub_organization
            ? claim.policy.sub_organization.id
            : null,
      },
    },
    {
      label: 'Claims History',
      shouldDisplay: true,
      IconComponent: HistoryIcon,
      iconColor: colors.buttonSelected,
      iconSize: 20,
      DialogComponent: RelatedClaimsCardDialog,
      dialogComponentProps: {
        claim,
        isDialog: true,
        onClose: () => setActionDialogDetails(false),
      },
    },
    {
      label: 'Manage External Claim Numbers',
      shouldDisplay: true,
      IconComponent: HashIcon,
      DialogComponent: ExternalClaimNumber,
      dialogComponentProps: {
        onClose: () => setActionDialogDetails(false),
      },
    },
    {
      label: 'Create Like',
      // Hidden rather than disabled without the permission: it is FORBIDDEN by default, so a disabled
      // entry would show to every user not granted it; `permissions` below still disables it under a
      // write freeze.
      // Configurable FNOL is required too: the source claim is only carried into the new claim by
      // NewFnolScreen, and the legacy AutoFnol / HomeFnolPage screens that render without it drop it,
      // which would create an unlinked claim right after the banner promised a link.
      shouldDisplay:
        canCreateLikeClaim && isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CONFIGURABLE_FNOL),
      IconComponent: CopyFileIcon,
      DialogComponent: CreateLikeClaimDialog,
      dialogComponentProps: {
        claim,
        onClose: () => setActionDialogDetails(false),
      },
      permissions: { action: PERMISSION_ACTIONS.CREATE_LIKE_CLAIM, verb: PERMISSION_VERBS.WRITE },
    },
  ];

  const handleDialogComponentClick = (DialogComponent, dialogComponentProps, mixpanelProps) => {
    setActionDialogDetails({ DialogComponent, dialogComponentProps });
    close();
    if (mixpanelProps) {
      mixpanel.track(mixpanelProps.mixpanel_event, { source: mixpanelProps.source });
    }
  };

  const handleMinimizedDialogComponentClick = ({
    DialogComponent,
    dialogComponentProps,
    extendMinimizedDialogProps,
    mixpanelProps,
  }) => {
    const id = uuidv4();

    const minimizedDialogProps = extendMinimizedDialogProps(id);
    const { handleCloseDialog } = add(
      merge(
        {
          dialogProps: {
            onMinimized: () => {
              mixpanel.track(MIXPANEL_EVENTS.MINIMIZED_DIALOG, {
                source: mixpanelProps?.source,
                type: minimizedDialogProps.type,
              });
            },
          },
        },
        {
          dialogComponent: DialogComponent,
          dialogProps: dialogComponentProps,
        },
        minimizedDialogProps
      )
    );
    dialogIdToHandleCloseRef.current[id] = handleCloseDialog;

    close();
    if (mixpanelProps) {
      mixpanel.track(mixpanelProps.mixpanel_event, { source: mixpanelProps.source });
    }
  };

  return (
    <>
      <Button
        className={`${classes.actionButton} ${classes.button}`}
        variant="contained"
        color="primary"
        endIcon={<ExpandMoreIcon />}
        {...triggerProps}
      >
        Actions
      </Button>

      <Popover {...popoverProps} aria-label="Claim actions" align="start">
        <div style={{ width: 290, padding: '15px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {actions
            .filter((action) => action.shouldDisplay)
            .map(
              (
                {
                  label,
                  IconComponent,
                  DialogComponent,
                  dialogComponentProps,
                  mixpanelProps,
                  isAllowedForReadOnly = false,
                  permissions,
                  extendMinimizedDialogProps,
                  adornment = null,
                },
                idx
              ) => (
                <Fragment key={idx}>
                  <PermissionsButtonWrapper {...permissions}>
                    <ActionMenuButton
                      label={label}
                      onClick={() => {
                        extendMinimizedDialogProps
                          ? handleMinimizedDialogComponentClick({
                              DialogComponent,
                              dialogComponentProps,
                              extendMinimizedDialogProps,
                              mixpanelProps,
                            })
                          : handleDialogComponentClick(DialogComponent, dialogComponentProps, mixpanelProps);
                      }}
                      IconComponent={IconComponent}
                      adornment={adornment}
                      disabled={isClaimWriteDisabled(claim, user, { allowOnClosedClaim: true, isAllowedForReadOnly })}
                    />
                  </PermissionsButtonWrapper>
                </Fragment>
              )
            )}
        </div>
      </Popover>
      {actionDialogDetails && <actionDialogDetails.DialogComponent {...actionDialogDetails.dialogComponentProps} />}
    </>
  );
}

function TimeOfLossContainer(props) {
  const { claim, onClaimUpdate } = props;
  const { user } = useCms();

  const [openDateDialog, setOpenDateDialog] = useState(false);
  const { classes } = useStyles();

  const lossString = ['wc_claim', 'gl_claim', 'pet_claim'].includes(claim.type) ? 'Incident' : 'Loss';

  const userPermissions = useHasMultiplePermissions({
    actions: [PERMISSION_ACTIONS.DATE_OF_LOSS, PERMISSION_ACTIONS.TIME_OF_LOSS],
    verb: PERMISSION_VERBS.WRITE,
  });
  const hasAnyOfPermissions = Object.values(userPermissions).some((hasPermission) => hasPermission);

  const sanitizePayload = useSanitizeByPermissions({
    fieldsPermissionActions: {
      date_of_loss: PERMISSION_ACTIONS.DATE_OF_LOSS,
      time_of_loss: PERMISSION_ACTIONS.TIME_OF_LOSS,
    },
    verb: PERMISSION_VERBS.WRITE,
  });

  const getUpdateRouteSuffix = () => {
    if (userPermissions[PERMISSION_ACTIONS.DATE_OF_LOSS] && userPermissions[PERMISSION_ACTIONS.TIME_OF_LOSS]) {
      return 'datetime_of_loss';
    } else if (userPermissions[PERMISSION_ACTIONS.DATE_OF_LOSS]) {
      return 'date_of_loss';
    } else if (userPermissions[PERMISSION_ACTIONS.TIME_OF_LOSS]) {
      return 'time_of_loss';
    }
    reportErrorInProductionOrThrow('No permissions to update date or time of loss, but update function was called');
  };

  return (
    <>
      <HoverActionField
        disabled={isClaimWriteDisabled(claim, user, { allowOnClosedClaim: true })}
        onAction={() => setOpenDateDialog(true)}
      >
        {isoDateToUs(claim.incident.date_of_loss)}
        {claim.incident.time_of_loss ? ' ' + timeToLocalTime(claim.incident.time_of_loss) : ''}
      </HoverActionField>
      {openDateDialog && (
        <Formik
          initialValues={{ date_of_loss: claim.incident.date_of_loss, time_of_loss: claim.incident.time_of_loss }}
          validationSchema={(() => {
            const allowFuture = isFutureDateOfLossAllowed(claim?.incident_configuration);
            let dateOfLossSchema = Yup.date().required('Required');

            if (!allowFuture) {
              dateOfLossSchema = dateOfLossSchema.max(new Date());
              if (claim?.reported_date) {
                dateOfLossSchema = dateOfLossSchema.max(claim?.reported_date, 'Must be before the Date of Report');
              }
            }

            return Yup.object().shape({
              date_of_loss: dateOfLossSchema,
              time_of_loss: Yup.string()
                .matches(/^([0-1][0-9]|2[0-3]):[0-5][0-9](|:[0-5][0-9])$/, 'Invalid time supplied')
                .nullable(),
            });
          })()}
          enableReinitialize
          onSubmit={async (values, formikProps) => {
            try {
              const payload = sanitizePayload(values);
              const routeSuffix = getUpdateRouteSuffix();
              await axios.put(`/api/v1/claims/${claim.id}/${routeSuffix}`, payload);
              await onClaimUpdate();
              setOpenDateDialog(false);
              formikProps.setSubmitting(false);
            } catch (error) {
              await reportAxiosError(error);
              formikProps.setSubmitting(false);
            }
          }}
        >
          {(formikProps) => (
            <CardDialog
              isDialog={true}
              open={openDateDialog}
              title={`Edit Time of ${lossString}`}
              maxWidth="sm"
              onClose={() => setOpenDateDialog(false)}
              preventClose={formikProps.isSubmitting}
            >
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <RestrictedPermissions action={PERMISSION_ACTIONS.DATE_OF_LOSS} verb={PERMISSION_VERBS.WRITE}>
                    <PermissionsButtonWrapper>
                      <DatePickerTextFieldFormik
                        id="date_of_loss"
                        label={`Date of ${lossString}`}
                        disableFuture={!isFutureDateOfLossAllowed(claim?.incident_configuration)}
                        className={classes.textField}
                        fullWidth
                      />
                    </PermissionsButtonWrapper>
                  </RestrictedPermissions>
                </Grid>
                <Grid item xs={6}>
                  <RestrictedPermissions action={PERMISSION_ACTIONS.TIME_OF_LOSS} verb={PERMISSION_VERBS.WRITE}>
                    <PermissionsButtonWrapper>
                      <TimePickerTextFieldFormik
                        id="time_of_loss"
                        label={`Time of ${lossString}`}
                        fullWidth
                        className={classes.textField}
                      />
                    </PermissionsButtonWrapper>
                  </RestrictedPermissions>
                </Grid>
              </Grid>
              <div className={classes.buttonsContainer}>
                <Button
                  variant="contained"
                  disabled={formikProps.isSubmitting || !hasAnyOfPermissions}
                  color="primary"
                  onClick={() => formikProps.handleSubmit()}
                >
                  Update
                </Button>
              </div>
            </CardDialog>
          )}
        </Formik>
      )}
    </>
  );
}

TimeOfLossContainer.propTypes = {
  claim: PropTypes.object.isRequired,
  onClaimUpdate: PropTypes.func.isRequired,
};

function PreferredContactContainer(props) {
  const { claim, onClaimUpdate } = props;
  const { user } = useCms();
  const { organizationContactRolesDict } = useOrganization();

  const { classes } = useStyles();
  const acceptedRoles = getAllSearchableContactRoles(organizationContactRolesDict);

  return (
    <HoverChangeField
      name="primary_contact_id"
      customFieldIdName="primary_contact"
      value={claim.primary_contact_id || ''}
      label="Preferred Contact"
      validationSchema={Yup.string().required('Required')}
      onUpdate={async ({ primary_contact_id }) => {
        const url =
          claim.type === 'general_claim' ? `/api/v1/claims/${claim.id}` : `/api/v1/${claim.type}s/${claim.id}`;
        try {
          await axios.patch(url, { primary_contact_id });
          await onClaimUpdate();
        } catch (error) {
          reportAxiosError(error);
        }
      }}
      specialFieldType="contact"
      specialFieldAdditionalProps={{ acceptedRoles }}
      showOnly
      disabled={isClaimWriteDisabled(claim, user, { allowOnClosedClaim: true })}
    >
      {claim.primary_contact_id && (
        <ContactEntity
          classes={classes}
          contactId={claim.primary_contact_id}
          contactDisplayName={claim.primary_contact_full_name}
          inline
          maxWidth={150}
        />
      )}
    </HoverChangeField>
  );
}

PreferredContactContainer.propTypes = {
  claim: PropTypes.object.isRequired,
  onClaimUpdate: PropTypes.func.isRequired,
};

function PhoneRepClaimPage(props) {
  const { claim, classes } = props;

  return (
    <>
      <div className={claimPageBarClass}>
        <Table style={{ height: '100%', backgroundColor: '#F8F8F8' }}>
          <TableBody>
            <TableRow className={classes.claimPageBarTableRow}>
              <TableCell className={classes.tableCell}>
                <strong>Claim #:</strong>
                {claim.claim_id_display}
              </TableCell>
              <TableCell className={classes.tableCell}>
                <span>
                  <strong>Named Insured: </strong>
                  {claim.policy.insured_contact_full_name}
                </span>
              </TableCell>
              <TableCell className={classes.tableCell}>
                <strong>TOL:</strong>
                {isoDateToUs(claim.incident.date_of_loss)}
                {claim.incident.time_of_loss ? ' ' + timeToLocalTime(claim.incident.time_of_loss) : ''}
              </TableCell>
              {isPolicyNotFound(claim) ? (
                <TableCell className={classes.tableCell}>
                  <span style={{ color: 'red' }}>
                    <strong>{claim.close_reason_display_name || claim.close_note || 'Policy Not Found'}</strong>
                  </span>
                </TableCell>
              ) : (
                <TableCell className={classes.tableCell}>
                  <span>
                    <strong>Policy #:</strong>
                    &nbsp;
                    <span style={{ whiteSpace: 'nowrap' }}>{claim.policy.policy_number}</span>
                    {claim.policy.is_manually_filled ? ' (Manually filled)' : ''}
                  </span>
                </TableCell>
              )}
              <TableCell className={classes.tableCell}>
                <DocumentIncomingPhoneCallContainer />
              </TableCell>
              <TableCell className={classes.tableCell}>
                <span>
                  <strong>File Owner: </strong>
                  {claim.handling_adjuster}
                </span>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <div className={classes.claimPageBodyNoTabs}>
        <ClaimSummaryPhoneRep claim={claim} classes={classes} />
      </div>
    </>
  );
}

PhoneRepClaimPage.propTypes = {
  claim: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

function ClaimTitle({ claim, classes, isLoading }) {
  const { lobConfigurationsDict } = useLobConfiguration();

  return (
    <span style={{ display: 'flex', alignItems: 'center' }}>
      {claim.is_access_limited && (
        <AccountLockOutline color="secondary" style={{ marginRight: 5, fontSize: '1.5em' }} />
      )}
      {claim?.lob && lobConfigurationsDict ? (
        <div className="py-0 pr-8">
          {getLobIcon({ lob: claim?.lob, lobConfigurationsDict, iconProps: { size: 24 } })}
        </div>
      ) : null}

      {claim.claim_id_display}
      {((claim.is_closed && (claim.close_reason_display_name || claim.close_note)) || isPolicyNotFound(claim)) && (
        <Typography display="inline" variant="body1" color="secondary" style={{ paddingLeft: 6 }}>
          <strong>{claim.close_reason_display_name || claim.close_note || 'Policy Was Not Found'}</strong>
        </Typography>
      )}
      {!isLoading && claim.type === 'gl_claim' && <GLStatusTimeline claim={claim} classes={classes} />}
      {(claim.type === 'gl_claim' || claim.type === 'wc_claim') && (
        <MedicareEligibleChip claim={claim} classes={classes} />
      )}
    </span>
  );
}

ClaimTitle.propTypes = {
  claim: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

function GLStatusTimeline({ claim, classes }) {
  const { incidentTypesDict } = useOrganization();

  return (
    <>
      {claim.prev_subtype && (
        <>
          <Chip size="small" label={incidentTypesDict.gl_claim[claim.prev_subtype].desc} className={classes.chip} />→
        </>
      )}
      <Chip
        size="small"
        label={incidentTypesDict.gl_claim[claim.subtype].desc}
        color="primary"
        className={classes.chip}
      />
    </>
  );
}

GLStatusTimeline.propTypes = {
  claim: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

const getMedicareEligibleStatus = (claim) => {
  if (claim.type !== 'gl_claim' && claim.type !== 'wc_claim') {
    return null;
  }
  const claimantParty = claim.type === 'gl_claim' ? claim.claimant_party : claim.employee_party;
  return claimantParty.involved_person.is_medicare_eligible;
};

function MedicareEligibleChip({ claim, classes }) {
  const medicareEligibleStatus = getMedicareEligibleStatus(claim);
  if (medicareEligibleStatus) {
    return (
      <Chip
        size="small"
        label="Medicare Eligible"
        className={classes.chip}
        style={{ backgroundColor: '#1A9C9E', color: 'white' }}
      />
    );
  }
  return null;
}

MedicareEligibleChip.propTypes = {
  claim: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
};

const ClaimSearchDialog = ({ onClose, claimIdDisplay, subOrganizationId }) => {
  const { classes } = useStyles();
  const { reportToClaimSearch } = useClaimSearch();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      await reportToClaimSearch(claimIdDisplay, subOrganizationId);
      setIsSuccess(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isSuccess ? (
        <SuccessMessageDialog title="Claim was sent successfully" onClose={onClose} />
      ) : (
        <CardDialog
          isDialog
          maxWidth="sm"
          fullWidth
          onClose={onClose}
          title="Report to ClaimSearch"
          preventClose={isLoading}
        >
          <Text variant={Text.VARIANTS.MD} weight={Text.WEIGHTS.REGULAR}>
            Updated claim data will be sent to ClaimSearch <br /> and a new search will be initiated
          </Text>
          <div className={classes.buttonsContainer}>
            <CancelButton onClick={onClose} disabled={isLoading} />
            <Button variant="contained" color="primary" onClick={handleSubmit} disabled={isLoading}>
              Send
            </Button>
          </div>
        </CardDialog>
      )}
    </>
  );
};

ClaimSearchDialog.propTypes = {
  onClose: PropTypes.func.isRequired,
  claimIdDisplay: PropTypes.string.isRequired,
  subOrganizationId: PropTypes.number,
};

const ClaimPageWithContext = compose(withOrganization, withCmsContext, withClaim, withRouter, (C) =>
  withStyles(C, styles)
)(ClaimPage);

const SafeClaimPage = withSafeComponentPage(ClaimPageWithContext, {
  location: 'claim_page',
  criticalityLevel: CriticalityLevel.HIGHEST,
  fallbackComponent: <FallbackErrorPage />,
});

export {
  ClaimActionButtonContainer,
  SafeClaimPage as ClaimPage,
  ClaimPageBar,
  PreferredContactContainer,
  TimeOfLossContainer,
};
